import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const AdminCarrierPermissionsPage = () => {
  const { hasPermission } = useAuth();
  const [carriers, setCarriers] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedCarrier, setSelectedCarrier] = useState(null);
  const [selectedCarrierPermissions, setSelectedCarrierPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Modals
  const [showCreatePermissionModal, setShowCreatePermissionModal] = useState(false);
  const [showEditPermissionModal, setShowEditPermissionModal] = useState(false);
  const [showDeletePermissionModal, setShowDeletePermissionModal] = useState(false);
  const [showAssignPermissionsModal, setShowAssignPermissionsModal] = useState(false);

  // Form data
  const [permissionFormData, setPermissionFormData] = useState({
    permission_name: '',
    description: '',
    resource_type: '',
    action_type: ''
  });
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
  const [itemToEdit, setItemToEdit] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

  const fetchCarriers = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/admin/carriers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCarriers(response.data.carriers || []);
    } catch (err) {
      console.error('Error fetching carriers:', err);
      setError('Failed to fetch carriers');
    }
  }, [API_BASE_URL]);

  const fetchPermissions = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/admin/permissions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPermissions(response.data.permissions || []);
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setError('Failed to fetch permissions');
    }
  }, [API_BASE_URL]);

  const fetchCarrierPermissions = useCallback(async (carrierId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/admin/carriers/${carrierId}/permissions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSelectedCarrierPermissions(response.data.permissions || []);
    } catch (err) {
      console.error('Error fetching carrier permissions:', err);
      setError('Failed to fetch carrier permissions');
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    if (hasPermission('system.admin')) {
      const loadData = async () => {
        setLoading(true);
        await Promise.all([fetchCarriers(), fetchPermissions()]);
        setLoading(false);
      };
      loadData();
    }
  }, [hasPermission, fetchCarriers, fetchPermissions]);

  useEffect(() => {
    if (selectedCarrier) {
      fetchCarrierPermissions(selectedCarrier.carrier_id);
    } else {
      setSelectedCarrierPermissions([]);
    }
  }, [selectedCarrier, fetchCarrierPermissions]);

  const handleCarrierClick = (carrier) => {
    setSelectedCarrier(carrier);
  };

  const handleCreatePermission = async () => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.post(`${API_BASE_URL}/admin/permissions`, permissionFormData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSuccessMessage('Permission created successfully');
      setShowCreatePermissionModal(false);
      setPermissionFormData({ permission_name: '', description: '', resource_type: '', action_type: '' });
      await fetchPermissions();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create permission');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleEditPermission = async () => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.put(`${API_BASE_URL}/admin/permissions/${itemToEdit.permission_id}`, permissionFormData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSuccessMessage('Permission updated successfully');
      setShowEditPermissionModal(false);
      setPermissionFormData({ permission_name: '', description: '', resource_type: '', action_type: '' });
      setItemToEdit(null);
      await fetchPermissions();
      if (selectedCarrier) {
        await fetchCarrierPermissions(selectedCarrier.carrier_id);
      }
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update permission');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleDeletePermission = async () => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${API_BASE_URL}/admin/permissions/${itemToDelete.permission_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSuccessMessage('Permission deleted successfully');
      setShowDeletePermissionModal(false);
      setItemToDelete(null);
      await fetchPermissions();
      if (selectedCarrier) {
        await fetchCarrierPermissions(selectedCarrier.carrier_id);
      }
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete permission');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleAssignPermissions = async () => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.put(`${API_BASE_URL}/admin/carriers/${selectedCarrier.carrier_id}/permissions`,
        { permission_ids: selectedPermissionIds },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setSuccessMessage('Carrier permissions updated successfully');
      setShowAssignPermissionsModal(false);
      setSelectedPermissionIds([]);
      await fetchCarrierPermissions(selectedCarrier.carrier_id);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update carrier permissions');
      setTimeout(() => setError(null), 5000);
    }
  };

  const openEditPermissionModal = (permission) => {
    setItemToEdit(permission);
    setPermissionFormData({
      permission_name: permission.permission_name,
      description: permission.description,
      resource_type: permission.resource_type,
      action_type: permission.action_type
    });
    setShowEditPermissionModal(true);
  };

  const openDeletePermissionModal = (permission) => {
    setItemToDelete(permission);
    setShowDeletePermissionModal(true);
  };

  const openAssignPermissionsModal = () => {
    const currentPermissionIds = selectedCarrierPermissions.map(p => p.permission_id);
    setSelectedPermissionIds(currentPermissionIds);
    setShowAssignPermissionsModal(true);
  };

  const togglePermissionSelection = (permissionId) => {
    setSelectedPermissionIds(prev =>
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  if (!hasPermission('system.admin')) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-12 bg-white rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading carrier permissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full p-6">
      <div className="w-full mx-auto flex-shrink-0">
        <h1 className="text-2xl font-bold mb-4">Carrier Permissions Management</h1>

        {successMessage && (
          <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
            {successMessage}
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1 min-h-0">
          {/* Carriers Panel */}
          <div className="bg-white rounded-lg shadow p-4 flex flex-col min-h-0">
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h2 className="text-lg font-semibold">Carriers</h2>
            </div>

            <div className="space-y-2 overflow-y-auto flex-1 min-h-0">
              {carriers.map((carrier) => (
                <div
                  key={carrier.carrier_id}
                  className={`p-3 border rounded cursor-pointer hover:bg-gray-50 ${
                    selectedCarrier?.carrier_id === carrier.carrier_id ? 'bg-blue-50 border-blue-500' : ''
                  }`}
                  onClick={() => handleCarrierClick(carrier)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-semibold">{carrier.carrier_name}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {carrier.is_active ? (
                          <span className="text-green-600">Active</span>
                        ) : (
                          <span className="text-red-600">Inactive</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Assigned Permissions Panel */}
          <div className="bg-white rounded-lg shadow p-4 flex flex-col min-h-0">
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h2 className="text-lg font-semibold">
                {selectedCarrier ? `${selectedCarrier.carrier_name} Permissions` : 'Select a Carrier'}
              </h2>
              {selectedCarrier && (
                <button
                  onClick={openAssignPermissionsModal}
                  className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                >
                  Manage
                </button>
              )}
            </div>

            {selectedCarrier ? (
              <div className="space-y-2 overflow-y-auto flex-1 min-h-0">
                {selectedCarrierPermissions.length > 0 ? (
                  selectedCarrierPermissions.map((permission) => (
                    <div key={permission.permission_id} className="p-3 border rounded">
                      <div className="font-semibold">{permission.permission_name}</div>
                      <div className="text-sm text-gray-600">{permission.description}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {permission.resource_type} / {permission.action_type}
                      </div>
                      {permission.granted_by_name && (
                        <div className="text-xs text-gray-400 mt-2">
                          Granted by: {permission.granted_by_name}
                        </div>
                      )}
                      {permission.notes && (
                        <div className="text-xs text-gray-400 mt-1">
                          Notes: {permission.notes}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    No permissions assigned to this carrier
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                Click on a carrier to view its permissions
              </div>
            )}
          </div>

          {/* All Permissions Panel */}
          <div className="bg-white rounded-lg shadow p-4 flex flex-col min-h-0">
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h2 className="text-lg font-semibold">All Permissions</h2>
              <button
                onClick={() => setShowCreatePermissionModal(true)}
                className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
              >
                + New
              </button>
            </div>

            <div className="space-y-2 overflow-y-auto flex-1 min-h-0">
              {permissions.map((permission) => (
                <div key={permission.permission_id} className="p-3 border rounded">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-semibold">{permission.permission_name}</div>
                      <div className="text-sm text-gray-600">{permission.description}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {permission.resource_type} / {permission.action_type}
                      </div>
                    </div>
                    <div className="flex space-x-1 ml-2">
                      <button
                        onClick={() => openEditPermissionModal(permission)}
                        className="px-2 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => openDeletePermissionModal(permission)}
                        className="px-2 py-1 text-xs bg-red-200 text-red-700 rounded hover:bg-red-300"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Create Permission Modal */}
      {showCreatePermissionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Create Permission</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Permission Name *</label>
                <input
                  type="text"
                  value={permissionFormData.permission_name}
                  onChange={(e) => setPermissionFormData({...permissionFormData, permission_name: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="e.g., content.edit"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  value={permissionFormData.description}
                  onChange={(e) => setPermissionFormData({...permissionFormData, description: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  rows="2"
                  placeholder="Description of this permission"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Resource Type *</label>
                <input
                  type="text"
                  value={permissionFormData.resource_type}
                  onChange={(e) => setPermissionFormData({...permissionFormData, resource_type: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="e.g., content, user, system"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Action Type *</label>
                <input
                  type="text"
                  value={permissionFormData.action_type}
                  onChange={(e) => setPermissionFormData({...permissionFormData, action_type: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="e.g., read, write, delete, admin"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowCreatePermissionModal(false);
                  setPermissionFormData({ permission_name: '', description: '', resource_type: '', action_type: '' });
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreatePermission}
                disabled={!permissionFormData.permission_name || !permissionFormData.description ||
                          !permissionFormData.resource_type || !permissionFormData.action_type}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Permission Modal */}
      {showEditPermissionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Edit Permission</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Permission Name *</label>
                <input
                  type="text"
                  value={permissionFormData.permission_name}
                  onChange={(e) => setPermissionFormData({...permissionFormData, permission_name: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  value={permissionFormData.description}
                  onChange={(e) => setPermissionFormData({...permissionFormData, description: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  rows="2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Resource Type *</label>
                <input
                  type="text"
                  value={permissionFormData.resource_type}
                  onChange={(e) => setPermissionFormData({...permissionFormData, resource_type: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Action Type *</label>
                <input
                  type="text"
                  value={permissionFormData.action_type}
                  onChange={(e) => setPermissionFormData({...permissionFormData, action_type: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowEditPermissionModal(false);
                  setPermissionFormData({ permission_name: '', description: '', resource_type: '', action_type: '' });
                  setItemToEdit(null);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleEditPermission}
                disabled={!permissionFormData.permission_name || !permissionFormData.description ||
                          !permissionFormData.resource_type || !permissionFormData.action_type}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Permission Modal */}
      {showDeletePermissionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Delete Permission</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the permission "{itemToDelete?.permission_name}"?
              <br /><br />
              <span className="text-red-600 font-semibold">This will remove this permission from all carriers and user types.</span>
            </p>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => { setShowDeletePermissionModal(false); setItemToDelete(null); }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeletePermission}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Permissions Modal */}
      {showAssignPermissionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              Manage Permissions for {selectedCarrier?.carrier_name}
            </h3>

            <p className="text-sm text-gray-600 mb-4">
              Select the permissions you want to assign to this carrier. Selected permissions are checked.
              These permissions will be automatically granted to all users within this carrier.
            </p>

            <div className="space-y-2 mb-6">
              {permissions.map((permission) => (
                <div key={permission.permission_id} className="flex items-start p-3 border rounded hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={selectedPermissionIds.includes(permission.permission_id)}
                    onChange={() => togglePermissionSelection(permission.permission_id)}
                    className="mt-1 mr-3"
                  />
                  <div className="flex-1">
                    <div className="font-semibold">{permission.permission_name}</div>
                    <div className="text-sm text-gray-600">{permission.description}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {permission.resource_type} / {permission.action_type}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => { setShowAssignPermissionsModal(false); setSelectedPermissionIds([]); }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignPermissions}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                Save Permissions
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCarrierPermissionsPage;
